import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { BrowserRouter as Router, Route, Routes, useParams } from "react-router-dom";

import AdminPanel from "./pages/AdminPanel";
import SocialGalleryPage from "./pages/SocialGalleryPage";
import SearchAI from "./pages/SearchAI";
import UploadImg from "./pages/UploadImg";
import TheGallery from "./pages/TheGallery";
import LandingPage from "./pages/LandingPage";
import PhotographerLandingPage from "./pages/PhotographerLandingPage";
import UserLandingPage from "./pages/UserLandingPage";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PricingPlans from "./pages/PricingPlans";
import About from "./pages/About";
import Checkout from "./pages/Checkout";
import HomeAdmin from "./pages/HomeAdmin";
import UserSettings from "./pages/UserSettings";
import AdminDashboard from "./pages/AdminDashboard";
import SignupForm from "./pages/SignupForm";
import SocialEventsPage from "./pages/SocialEventsPage";
import SupportPage from "./pages/SupportPage";
import SharedAlbumPage from "./pages/SharedAlbumPage";
import AdminSupportPage from "./pages/AdminSupportPage";
import ShareApp from "./pages/share/ShareApp";
import { isShareHost } from "./utils/appHost";

import EventsAdmin from "./components/EventsAdmin";
import Header from "./components/Header";
import HeaderAdmin from "./components/HeaderAdmin";
import HeaderLanding from "./components/HeaderLanding";
import AdminLayout from "./components/AdminLayout";
import EventPublicNav from "./components/EventPublicNav";
import Tail from "./components/Tail";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import PublicPageWrapper from "./components/PublicPageWrapper";
import LoginForm from "./components/LoginForm";
import { useTranslation } from "react-i18next";

import { AuthProvider, useAuth } from "./AuthContext";
import "./i18n";
import { usePageTracking } from "./hooks/usePageTracking";

function EventOverviewRoute({ user, page_name, setEvent, authenticated, logout }) {
  const { id_name } = useParams();
  const { t } = useTranslation();
  return (
    <AdminLayout>
      <>
        <HeaderAdmin
          page_name={page_name}
          setEvent={setEvent}
          isAuthenticated={authenticated}
          onLogout={logout}
          user={user}
        />
        <EventsAdmin user={user} selectedE={id_name} />
      </>
    </AdminLayout>
  );
}

function AppRoutes({
  event,
  setEvent,
  page_name,
  setPageName,
  selectedE,
  setSelectedE,
  selectedAlbum,
  setSelectedAlbum,
}) {
  const { t } = useTranslation();
  const { authenticated, loadingAuth, checkAuth, logout, user } = useAuth();
  usePageTracking();

  const socialLinks = {
    instagram: event.user?.social_urls?.insta || null,
    facebook: event.user?.social_urls?.fb || null,
    website: event.user?.social_urls?.website || null,
  };

  if (loadingAuth) {
    return <Loader iconPath="/favicon.png" size={100} />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <div className="d-flex flex-column justify-content-center min-vh-100">
            <LoginForm onLogin={checkAuth} />
          </div>
        }
      />
      <Route
        path="/signup"
        element={
          <>
            <HeaderAdmin
              page_name={page_name}
              isAuthenticated={authenticated}
            />
            <div className="">
              <SignupForm onSignup={checkAuth} />
            </div>
          </>
        }
      />

      {/* ── Admin routes — wrapped in AdminLayout ── */}
      <Route
        path="/admin"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={t("dashboard")}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <HomeAdmin user={user} />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/events"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={t("my_events")}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <EventsAdmin user={user} selectedE={selectedE} />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/support"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={t("support.page_title")}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <SupportPage />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/support"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={t("support.admin_title")}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <AdminSupportPage />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/overview/:id_name"
        element={
          <EventOverviewRoute
            user={user}
            page_name={page_name}
            setEvent={setEvent}
            authenticated={authenticated}
            logout={logout}
          />
        }
      />

      <Route
        path="/admin/:id_name"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={page_name}
                setEvent={setEvent}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <Container>
                <div>
                  {event !== false ? (
                    event.name ? (
                      <AdminPanel
                        event={event}
                        setPageName={setPageName}
                        setSelectedE={setSelectedE}
                      />
                    ) : (
                      <Loader iconPath="/favicon.png" size={100} />
                    )
                  ) : (
                    <h2 className="text-center mb-4">Event Not Found</h2>
                  )}
                </div>
              </Container>
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/upload/:id_name"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={page_name}
                setEvent={setEvent}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <UploadImg event={event} setPageName={setPageName} />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name={t("header_admin.settings")}
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <UserSettings />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name="Admin Dashboard"
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <AdminDashboard />
            </>
          </AdminLayout>
        }
      />

      <Route
        path="/social-events"
        element={
          <AdminLayout>
            <>
              <HeaderAdmin
                page_name="Social Events"
                isAuthenticated={authenticated}
                onLogout={logout}
                user={user}
              />
              <SocialEventsPage />
            </>
          </AdminLayout>
        }
      />

      {/* ── Public info pages ── */}
      <Route
        path="/plans"
        element={
          <PublicPageWrapper
            authenticated={authenticated}
            user={user}
            logout={logout}
            pageName="Pricing Plans"
          >
            <PricingPlans page_name="pricing" />
          </PublicPageWrapper>
        }
      />

      <Route
        path="/about"
        element={
          <PublicPageWrapper
            authenticated={authenticated}
            user={user}
            logout={logout}
            pageName="About"
          >
            <About />
          </PublicPageWrapper>
        }
      />

      <Route
        path="/checkout"
        element={
          <PublicPageWrapper
            authenticated={authenticated}
            user={user}
            logout={logout}
            pageName="Checkout"
          >
            <Checkout />
          </PublicPageWrapper>
        }
      />

      {/* ── Public event pages — with EventPublicNav ── */}
      <Route
        path="/event/:id_name"
        element={
          <div className="bg-light">
            <div>
              <Header
                setEvent={setEvent}
                showUrls={true}
                social_urls={socialLinks}
              />
              <EventPublicNav hideOnDesktop />
              <div className="event-page-content">
                {event !== false ? (
                  event.name ? (
                    <SearchAI event={event} />
                  ) : (
                    <Loader iconPath="/favicon.png" size={100} />
                  )
                ) : (
                  <h2 className="text-center mb-4">Event Not Found</h2>
                )}
              </div>
            </div>
          </div>
        }
      />

      <Route
        path="/event/:id_name/gallery"
        element={
          <div className="bg-light">
            <div>
              <Header
                setEvent={setEvent}
                showUrls={false}
                social_urls={socialLinks}
                onAlbumChange={setSelectedAlbum}
                selectedAlbum={selectedAlbum}
              />
              <EventPublicNav hideOnDesktop />
              <Container className="event-page-content">
                <div>
                  {event !== false ? (
                    event.name ? (
                      <TheGallery event_id={event.event_id} event={event} selectedAlbum={selectedAlbum} onAlbumChange={setSelectedAlbum} />
                    ) : (
                      <Loader iconPath="/favicon.png" size={100} />
                    )
                  ) : (
                    <h2 className="text-center mb-4">Event Not Found</h2>
                  )}
                </div>
              </Container>
            </div>
          </div>
        }
      />

      <Route path="/event/:id_name/social" element={<SocialGalleryPage />} />

      {/* Public shared album — standalone, no event header/nav (no path to the full gallery) */}
      <Route path="/album/:token" element={<SharedAlbumPage />} />

      <Route path="/landing-old" element={<LandingPage />} />
      <Route path="/" element={<PhotographerLandingPage />} />
      <Route path="/landing-gu" element={<UserLandingPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
    </Routes>
  );
}

function App() {
  const [event, setEvent] = useState({});
  const [page_name, setPageName] = useState("");
  const [selectedE, setSelectedE] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  // share.pic-loud.com: file transfers app (same build, own routes)
  if (isShareHost) {
    return (
      <Router>
        <AuthProvider>
          <ShareApp />
        </AuthProvider>
      </Router>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Router>
        <AuthProvider>
          <main className="main-content flex-grow-1">
            <AppRoutes
              event={event}
              setEvent={setEvent}
              page_name={page_name}
              setPageName={setPageName}
              selectedE={selectedE}
              setSelectedE={setSelectedE}
              selectedAlbum={selectedAlbum}
              setSelectedAlbum={setSelectedAlbum}
            />
          </main>
        </AuthProvider>
        {event.user?.social_urls ? (
          // <Footer
          //   social_urls={{
          //     instagram: event.user?.social_urls?.insta || null,
          //     facebook: event.user?.social_urls?.fb || null,
          //     website: event.user?.social_urls?.website || null,
          //   }}
          // />
          <>  </>
        ) : page_name !== "" ? (
          <Tail />
        ) : null}
      </Router>
    </div>
  );
}

export default App;
